/**
 * "Assign a resident a pendant" guided flow — via the easy learn-in path.
 *
 * Teaches the quick-add route (Decision 3): residents list → Manage Devices →
 * Available tab → Add New Device → pick a device → serial or Listen → Add &
 * Assign. That path exists precisely so nobody has to leave the resident to go
 * add a device on the Devices page, which is the confusion it was built to
 * remove — so it's the path worth teaching.
 *
 * Two things make this flow different from the other three:
 *
 *  1. **It completes two ways.** If the serial is already in the system, the easy
 *     drawer disables Add & Assign and offers to assign the existing device
 *     instead. That's a different transaction, so completion is an `any`-of over
 *     both. See §6.4.
 *
 *  2. **It can take minutes, not seconds.** The Listen window alone runs 60s
 *     while someone walks to the device and presses its button, so the last
 *     step's timeout is generous. The timeout only surfaces the "still working?"
 *     affordance — it cancels nothing.
 *
 * The guide deliberately stops spotlighting once inside the easy drawer beyond
 * its two waypoints (catalog, serial): that drawer is already an opinionated
 * wizard with its own step progression, and per-element rings there would be
 * chrome on top of chrome.
 *
 * See docs/INTERACTIVE_HELP_PLAN.md §6.3 (steps) and §6.4 (completion paths).
 */

export default {
    id: 'assign-pendant',
    title: 'Assign a pendant to a resident',
    description: 'Add or assign a pendant to a resident.',

    minRoleWeight: 11,

    /* Unlike the lifecycle flows, this one stays available on EHR-active
       campuses: an EHR drives resident records, not device assignment, so this
       is still the portal's job there (Decision 2). */
    available: () => true,

    startsAt: 'This will take you to the Residents page.',

    steps: [
        {
            id: 'open-row-menu',
            route: { path: '/community/residents', navigate: true },
            anchor: 'residents.row-actions',
            title: 'Open the resident’s row menu',
            body: 'Find the resident getting the pendant and click the ⋮ menu at the end of their row.',
            placement: 'left',
            /* The menu item only renders while the menu is open, so its presence
               is exactly "the menu is open" — same rule as the move-out flow. */
            done: { type: 'dom', selector: '[data-help-id="row-actions.manage-devices"]' },
        },
        {
            id: 'choose-manage-devices',
            route: { path: '/community/residents', navigate: false },
            anchor: 'row-actions.manage-devices',
            title: 'Choose Manage Devices',
            body: 'Click Manage Devices to see everything assigned to this resident.',
            placement: 'left',
            /* Closing the menu without choosing sends the user back to re-open it.
               Clicking Manage Devices closes the menu and opens the drawer in the
               same tick; the engine's regression guard makes completion win that
               race (useContextRegression). */
            context: {
                selector: '[data-help-id="row-actions.manage-devices"]',
                contextStep: 'open-row-menu',
            },
            /* The tab bar renders as soon as the drawer opens, on either tab, so
               the Available tab button appearing on screen means "drawer open".
               Note this drawer is always mounted and parked off-screen, so the
               engine's visibility rule (not mere existence) is what makes this
               work — see detectors.js selectorVisible. */
            done: { type: 'dom', selector: '[data-help-id="device-drawer.available-tab"]' },
        },
        {
            id: 'available-tab',
            route: { path: '/community/residents', navigate: false },
            anchor: 'device-drawer.available-tab',
            title: 'Switch to the Available tab',
            body: 'It opens on Assigned. Click Available to reach the devices you can assign — and the option to add a brand-new one.',
            placement: 'left',
            context: {
                selector: '[data-help-id="device-drawer.available-tab"]',
                contextStep: 'choose-manage-devices',
            },
            /* Add New Device renders only on the Available tab, so its presence
               is exactly "the user switched tabs". */
            done: { type: 'dom', selector: '[data-help-id="device-drawer.add-new"]' },
        },
        {
            id: 'add-new',
            route: { path: '/community/residents', navigate: false },
            anchor: 'device-drawer.add-new',
            title: 'Click Add New Device',
            body: 'Click Add New Device — even if the pendant might already be in the system. The next screen handles that case for you.',
            placement: 'left',
            context: {
                selector: '[data-help-id="device-drawer.add-new"]',
                contextStep: 'available-tab',
            },
            done: { type: 'dom', selector: '[data-help-id="easy-add.resident.catalog"]' },
        },
        {
            id: 'pick-device',
            route: { path: '/community/residents', navigate: false },
            anchor: 'easy-add.resident.catalog',
            title: 'Pick the kind of device',
            body: 'Choose the pendant from the pictured devices, or find it in the Other devices list below them.',
            placement: 'left',
            /* Backing out of the easy drawer returns to the button that opens it. */
            context: {
                selector: '[data-help-id="easy-add.resident.catalog"]',
                contextStep: 'add-new',
            },
            /* Picking advances the drawer's internal step from 'device' to
               'serial', which unmounts the catalog and mounts the serial field. */
            done: { type: 'dom', selector: '[data-help-id="easy-add.resident.serial"]' },
        },
        {
            id: 'identify-and-add',
            route: { path: '/community/residents', navigate: false },
            anchor: 'easy-add.resident.serial',
            title: 'Identify it, then Add & Assign',
            body: 'Type the serial from the device label, or click Listen for device and press the pendant’s button. Then click Add & Assign. If that serial is already in the system you’ll be offered that device instead — assigning it finishes this guide too.',
            placement: 'left',
            context: {
                selector: '[data-help-id="easy-add.resident.serial"]',
                contextStep: 'add-new',
            },
            /* Two exits, either one completes (§6.4):
                 device_add_resident    — new device added and assigned in one call
                                          (ResidentsPage handleEasyAddDevice → addDevice)
                 device_assign_resident — serial already existed, so the drawer
                                          assigned that device instead
                                          (handleEasyAssignExisting → assignDevice,
                                           residentsApi.js:367)
               The second also covers a user who ignores the taught path and uses
               the drawer's plain search-and-Assign list — the guide still
               completes, because the taught path is a recommendation, not a cage. */
            done: {
                any: [
                    { type: 'transaction', transTypes: ['device_add_resident'] },
                    { type: 'transaction', transTypes: ['device_assign_resident'] },
                ],
            },
            /* 90s: the Listen window alone is 60s, and someone may be walking to
               the device. Only surfaces the "still working?" hint. */
            timeoutMs: 90000,
        },
    ],

    success: {
        title: 'Pendant assigned',
        body: 'The device is assigned and active for this resident. Press its button once to confirm it reports in, then check the alert appears under Alerts.',
    },
};
